import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { RouteErrorBoundary } from "@/components/medam/route-error-boundary";
import { Marca } from "@/components/medam/marca";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loginWithBackend, registerAdminWithBackend } from "@/lib/backend-auth";

export const Route = createFileRoute("/acceso")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Acceso administrativo | MEDAM" },
      {
        name: "description",
        content: "Acceso privado para el equipo administrativo de MEDAM.",
      },
      { property: "og:title", content: "Acceso administrativo | MEDAM" },
      { property: "og:description", content: "Panel privado de gestión de posiciones MEDAM." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: ({ error, reset }) => (
    <RouteErrorBoundary
      error={error}
      reset={reset}
      titulo="No pudimos abrir el acceso administrativo"
      descripcion="Ocurrió un problema al cargar esta sección."
    />
  ),
  component: Acceso,
});

function Acceso() {
  const navigate = useNavigate();
  const [correo, setCorreo] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [modo, setModo] = useState<"entrar" | "crear">("entrar");
  const [cargando, setCargando] = useState(false);

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setCargando(true);
    try {
      if (modo === "entrar") {
        await loginWithBackend(correo.trim(), contrasena);
        await navigate({ to: "/admin", replace: true });
      } else {
        const nombre = correo.split("@")[0]?.trim() || "Administrador";
        await registerAdminWithBackend(correo.trim(), contrasena, nombre);
        toast.success("Cuenta creada. Ya puedes iniciar sesión.");
        setModo("entrar");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos completar el acceso.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/40 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-sm">
        <Marca />
        <h1 className="mt-6 font-display text-2xl">Acceso administrativo</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Área privada para el equipo administrativo de MEDAM responsable de las posiciones y postulaciones.
        </p>

        <form onSubmit={enviar} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="correo">Correo institucional</Label>
            <Input
              id="correo"
              type="email"
              required
              autoComplete="email"
              value={correo}
              onChange={(evento) => setCorreo(evento.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contrasena">Contraseña</Label>
            <Input
              id="contrasena"
              type="password"
              required
              minLength={8}
              autoComplete={modo === "entrar" ? "current-password" : "new-password"}
              value={contrasena}
              onChange={(evento) => setContrasena(evento.target.value)}
            />
          </div>
          <Button type="submit" className="w-full" disabled={cargando}>
            {cargando ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            {modo === "entrar" ? "Entrar al panel" : "Crear cuenta"}
          </Button>
        </form>

        <button
          type="button"
          className="mt-6 w-full text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={() => setModo(modo === "entrar" ? "crear" : "entrar")}
        >
          {modo === "entrar"
            ? "¿No tienes cuenta? Crear acceso"
            : "Ya tengo cuenta, quiero iniciar sesión"}
        </button>
      </div>
    </div>
  );
}
